import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrders } from '../../hooks/useOrders';
import { useAuth } from '../../context/AuthContext';
import { formatPnL, getPnLColor } from '../../utils/formatters';
import { STATUS_META } from '../../constants';
import styles from './Orders.module.css';

/* ══════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════ */
const fmt = (val, decimals = 2) =>
  val != null ? Number(val).toFixed(decimals) : '—';

const fmtCurrency = (val) =>
  val != null ? `₹${Number(val).toLocaleString('en-IN')}` : '—';

const fmtDate = (str) => {
  if (!str) return '—';
  return str.slice(0, 16).replace('T', ' ');
};

/* ── Pills ── */
function StatusPill({ status }) {
  const cls = {
    open:      styles.pillOpen,
    closed:    styles.pillClosed,
    partial:   styles.pillPartial,
    cancelled: styles.pillCancelled,
  };
  const label = STATUS_META[status]?.label ?? (status ?? '—').toUpperCase();
  return (
    <span className={`${styles.pill} ${cls[status] ?? styles.pillClosed}`}>
      {label}
    </span>
  );
}

function SignalPill({ signal }) {
  const s       = (signal ?? '').toLowerCase();
  const isShort = s === 'short' || s === 'sell';
  return (
    <span className={`${styles.pill} ${isShort ? styles.pillSell : styles.pillBuy}`}>
      {(signal ?? '—').toUpperCase()}
    </span>
  );
}

function BrokerPill({ name }) {
  const cls = name?.toLowerCase() === 'dhan' ? styles.pillDhan : styles.pillKite;
  return (
    <span className={`${styles.pill} ${cls}`}>
      {(name ?? '—').toUpperCase()}
    </span>
  );
}

/* ── Tooltip ── */
function Tooltip({ content, children }) {
  const [pos, setPos] = React.useState(null);
  const ref = React.useRef(null);
  return (
    <>
      <span
        ref={ref}
        style={{ display: 'inline-block' }}
        onMouseEnter={() => {
          if (ref.current && content) {
            const r = ref.current.getBoundingClientRect();
            setPos({ x: r.left + r.width / 2, y: r.bottom + 8 });
          }
        }}
        onMouseLeave={() => setPos(null)}
      >
        {children}
      </span>
      {pos && (
        <div style={{
          position:     'fixed',
          top:          pos.y,
          left:         pos.x,
          transform:    'translateX(-50%)',
          background:   '#080e1c',
          border:       '1px solid #2a3f5f',
          borderRadius: 8,
          padding:      '8px 12px',
          color:        '#e8edf5',
          fontSize:     11,
          fontFamily:   'DM Mono, monospace',
          whiteSpace:   'pre',
          pointerEvents:'none',
          zIndex:       9999,
          boxShadow:    '0 4px 20px rgba(0,0,0,0.6)',
          lineHeight:   1.6,
          letterSpacing:'0.3px',
        }}>
          {content}
        </div>
      )}
    </>
  );
}

/* ── Broker order status ── */
function OrderStatusBadge({ status, message }) {
  const isOk     = status === 'COMPLETE';
  const isFailed = status === 'failed' || status === 'FAILED';
  const color    = isOk ? '#00e5a0' : isFailed ? '#ff4d6d' : '#f5a623';
  return (
    <div className={styles.orderStatusWrap}>
      <span className={styles.orderStatusDot} style={{ background: color }} />
      <span style={{ color, fontSize: 11 }}>{status ?? '—'}</span>
      {message && <span className={styles.orderStatusMsg} title={message}>⚠</span>}
    </div>
  );
}

/* ══════════════════════════════════════════════
   LEG BLOCK  (inside drawer)
══════════════════════════════════════════════ */
function LegBlock({ label, leg, color }) {
  if (!leg) return null;
  const entry = leg.entry ?? {};
  const exit  = leg.exit  ?? {};

  return (
    <div className={styles.legBlock} style={{ '--leg-color': color }}>
      <div className={styles.legHeader}>
        <div className={styles.legHeaderLeft}>
          <span className={styles.legAccent} />
          <span className={styles.legLabel}>{label}</span>
          <span className={styles.legType} data-type={leg.option_type}>
            {leg.option_type}
          </span>
        </div>
        <span className={styles.legSymbol}>{leg.tradingsymbol ?? '—'}</span>
      </div>

      {/* Entry → Exit prices */}
      <div className={styles.legPriceRow}>
        <div className={styles.legPriceCard}>
          <span>Entry</span>
          <strong>{fmtCurrency(entry.price)}</strong>
          <small>{fmtDate(entry.placed_at)}</small>
        </div>
        <div className={styles.legArrow}>→</div>
        <div className={styles.legPriceCard}>
          <span>Exit</span>
          <strong>{fmtCurrency(exit.price)}</strong>
          <small>{fmtDate(exit.placed_at)}</small>
        </div>
        <div className={styles.legPnlCard} style={{ color: getPnLColor(leg.pnl) }}>
          <span>Leg PnL</span>
          <strong>{formatPnL(leg.pnl)}</strong>
        </div>
      </div>

      {/* Greeks */}
      <div className={styles.greeksGrid}>
        <div className={styles.greekItem}><span>Strike</span><strong>{leg.strike ?? '—'}</strong></div>
        <div className={styles.greekItem}><span>Qty</span><strong>{leg.quantity ?? '—'}</strong></div>
        <div className={styles.greekItem}>
          <span>IV</span>
          <strong>{entry.iv != null ? `${fmt(entry.iv, 1)}%` : '—'}</strong>
        </div>
        <div className={styles.greekItem}><span>Delta</span><strong>{fmt(entry.delta, 4)}</strong></div>
        <div className={styles.greekItem}><span>Theta</span><strong>{fmt(entry.theta, 4)}</strong></div>
        <div className={styles.greekItem}><span>Gamma</span><strong>{fmt(entry.gamma, 5)}</strong></div>
        <div className={styles.greekItem}><span>Vega</span><strong>{fmt(entry.vega, 4)}</strong></div>
        <div className={styles.greekItem}>
          <span>OI</span>
          <strong>{entry.oi != null ? Number(entry.oi).toLocaleString('en-IN') : '—'}</strong>
        </div>
        <div className={styles.greekItem}>
          <span>Fallback</span>
          <strong style={{ color: leg.fallback_used ? '#f5a623' : '#00e5a0' }}>
            {leg.fallback_used ? 'Yes' : 'No'}
          </strong>
        </div>
      </div>

      {/* Broker status */}
      <div className={styles.legBrokerRow}>
        <OrderStatusBadge
          status={entry.broker_order_status}
          message={entry.broker_order_message}
        />
        {entry.broker_order_id && (
          <span className={styles.brokerOrderId}>#{entry.broker_order_id}</span>
        )}
        {entry.broker_error_type && (
          <span className={styles.brokerErrorType}>{entry.broker_error_type}</span>
        )}
      </div>

      {entry.broker_order_message && (
        <div className={styles.brokerErrorMsg}>{entry.broker_order_message}</div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   ORDER DETAIL DRAWER
══════════════════════════════════════════════ */
function OrderDrawer({ order, onClose }) {
  if (!order) return null;
  const meta = order.selection_metadata ?? {};

  return (
    <div className={styles.drawerOverlay} onClick={onClose}>
      <aside className={styles.drawer} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.drawerHeader}>
          <div>
            <div className={styles.drawerInstrument}>
              {order.instrument}
              <span className={styles.drawerOptionType}>{order.option_type}</span>
            </div>
            <div className={styles.drawerMeta}>
              {order.expiry} · {order.strategy_name} · lot {order.lot_size}
            </div>
          </div>
          <div className={styles.drawerHeaderRight}>
            <StatusPill status={order.status} />
            <button className={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* PnL summary */}
        <div className={styles.drawerPnlRow}>
          <div className={styles.drawerPnlCard}>
            <span>Net Credit</span>
            <strong style={{ color: '#00e5a0' }}>{fmtCurrency(order.net_credit)}</strong>
          </div>
          <div className={styles.drawerPnlCard}>
            <span>Total PnL</span>
            <strong style={{ color: getPnLColor(order.total_pnl) }}>
              {formatPnL(order.total_pnl)}
            </strong>
          </div>
          <div className={styles.drawerPnlCard}>
            <span>Signal</span>
            <strong><SignalPill signal={order.signal} /></strong>
          </div>
        </div>

        {/* Info grid */}
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span>Broker</span>
            <strong><BrokerPill name={order.broker_name} /></strong>
          </div>
          <div className={styles.infoItem}>
            <span>Strategy</span>
            <strong>{order.strategy_type ?? '—'}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>In Broker</span>
            <strong style={{ color: order.order_placed_in_broker ? '#00e5a0' : '#ff4d6d' }}>
              {order.order_placed_in_broker ? '✓ Yes' : '✗ No'}
            </strong>
          </div>
          <div className={styles.infoItem}>
            <span>Close Reason</span>
            <strong>{order.closed_reason?.replace(/_/g, ' ') ?? '—'}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Opened</span>
            <strong>{fmtDate(order.created_at)}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Closed</span>
            <strong>{fmtDate(order.closed_at)}</strong>
          </div>
        </div>

        {/* Legs */}
        <div className={styles.legsSection}>
          <div className={styles.legsSectionTitle}>Legs</div>
          <LegBlock label="SELL LEG" leg={order.sell_leg} color="#ff4d6d" />
          <LegBlock label="BUY LEG"  leg={order.buy_leg}  color="#00e5a0" />
        </div>

        {/* Partial Exit (Add-on) */}
        {(order.addon_exit || order.squareoff_trigger) && (
          <div className={styles.metaSection}>
            <div className={styles.metaSectionTitle}>Partial Exit</div>
            <div className={styles.metaGrid}>
              {order.squareoff_trigger && (
                <div className={styles.metaItem}><span>Squareoff Trigger</span><strong style={{ color: '#f5a623' }}>{order.squareoff_trigger}</strong></div>
              )}
              {order.addon_exit?.trigger && (
                <div className={styles.metaItem}><span>Exit Trigger</span><strong style={{ color: '#f5a623' }}>{order.addon_exit.trigger}</strong></div>
              )}
              {order.addon_exit?.exit_qty != null && (
                <div className={styles.metaItem}><span>Exit Qty</span><strong>{order.addon_exit.exit_qty}</strong></div>
              )}
              {order.addon_exit?.exited_at && (
                <div className={styles.metaItem}><span>Exited At</span><strong>{fmtDate(order.addon_exit.exited_at)}</strong></div>
              )}
              {order.addon_exit?.broker_order_status && (
                <div className={styles.metaItem}>
                  <span>Broker Status</span>
                  <strong style={{ color: order.addon_exit.broker_order_status === 'COMPLETE' ? '#00e5a0' : '#f5a623' }}>
                    {order.addon_exit.broker_order_status}
                  </strong>
                </div>
              )}
              {order.addon_exit?.broker_order_id && (
                <div className={styles.metaItem}><span>Broker Order ID</span><strong style={{ fontSize: 11 }}>#{order.addon_exit.broker_order_id}</strong></div>
              )}
              {order.partial_profit != null && (
                <div className={styles.metaItem}>
                  <span>Partial Profit</span>
                  <strong style={{ color: getPnLColor(order.partial_profit) }}>{formatPnL(order.partial_profit)}</strong>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className={styles.metaSection}>
          <div className={styles.metaSectionTitle}>Selection Metadata</div>
          <div className={styles.metaGrid}>
            <div className={styles.metaItem}><span>VIX</span><strong>{meta.vix ?? '—'}</strong></div>
            <div className={styles.metaItem}><span>VIX Band</span><strong>{meta.vix_band_label ?? '—'}</strong></div>
            <div className={styles.metaItem}><span>DTE</span><strong>{meta.dte ?? '—'}</strong></div>
            <div className={styles.metaItem}><span>Delta Source</span><strong>{meta.sell_delta_source ?? '—'}</strong></div>
            <div className={styles.metaItem}><span>Delta Target</span><strong>{meta.sell_delta_target ?? '—'}</strong></div>
            <div className={styles.metaItem}>
              <span>Delta Actual</span>
              <strong>{meta.sell_delta_actual != null ? fmt(meta.sell_delta_actual, 4) : '—'}</strong>
            </div>
            <div className={styles.metaItem}><span>Buy Strike Calc</span><strong>{meta.buy_strike_calculated ?? '—'}</strong></div>
            <div className={styles.metaItem}><span>Buy Strike Final</span><strong>{meta.buy_strike_final ?? '—'}</strong></div>
            <div className={styles.metaItem}><span>Walk Count</span><strong>{meta.buy_walk_count ?? '—'}</strong></div>
            <div className={styles.metaItem}>
              <span>Retry Used</span>
              <strong style={{ color: meta.buy_retry_used ? '#f5a623' : '#00e5a0' }}>
                {meta.buy_retry_used ? 'Yes' : 'No'}
              </strong>
            </div>
          </div>
        </div>

      </aside>
    </div>
  );
}

/* ══════════════════════════════════════════════
   FILTER BAR
══════════════════════════════════════════════ */
function FilterBar({ filters, applyFilter, resetFilters, brokers = [], strategies = [], instrumentNames = [] }) {
  const hasActive = Object.values(filters).some(v => v !== '');
  return (
    <div className={styles.filterBar}>
      <div className={styles.filterGroup}>
        <label>Broker</label>
        <select value={filters.broker_id ?? ''} onChange={e => applyFilter('broker_id', e.target.value)}>
          <option value="">All brokers</option>
          {brokers.map(b => (
            <option key={b.broker_id} value={b.broker_id}>
              {b.display_name}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.filterGroup}>
        <label>Strategy</label>
        <select value={filters.strategy_id ?? ''} onChange={e => applyFilter('strategy_id', e.target.value)}>
          <option value="">All strategies</option>
          {strategies.map(st => (
            <option key={st.value} value={st.value}>{st.label}</option>
          ))}
        </select>
      </div>
      <div className={styles.filterGroup}>
        <label>Status</label>
        <select value={filters.status} onChange={e => applyFilter('status', e.target.value)}>
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="partial">Partial</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div className={styles.filterGroup}>
        <label>Signal</label>
        <select value={filters.signal} onChange={e => applyFilter('signal', e.target.value)}>
          <option value="">All</option>
          <option value="long">Long</option>
          <option value="short">Short</option>
        </select>
      </div>
      <div className={styles.filterGroup}>
        <label>Instrument</label>
        <select value={filters.instrument} onChange={e => applyFilter('instrument', e.target.value)}>
          <option value="">All instruments</option>
          {instrumentNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className={styles.filterGroup}>
        <label>From</label>
        <input
          type="date"
          value={filters.from_date}
          onChange={e => applyFilter('from_date', e.target.value)}
        />
      </div>
      <div className={styles.filterGroup}>
        <label>To</label>
        <input
          type="date"
          value={filters.to_date}
          onChange={e => applyFilter('to_date', e.target.value)}
        />
      </div>
      {hasActive && (
        <button className={styles.resetBtn} onClick={resetFilters}>✕ Clear</button>
      )}
    </div>
  );
}

/* ── Trigger quick-filter buttons ── */
function TriggerFilterBar({ value, onSelect }) {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 12 }}>
      <span style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', letterSpacing: '1px', marginRight: 4 }}>
        Exit Trigger:
      </span>
      {[
        { val: '',           label: 'All'      },
        { val: 'TARGET_HIT', label: '⊕ Target' },
        { val: 'SL_HIT',     label: '⊗ SL Hit' },
      ].map(({ val, label }) => (
        <button
          key={val}
          onClick={() => onSelect(val)}
          style={{
            padding: '5px 10px', fontSize: 10, fontWeight: 700,
            fontFamily: 'DM Mono, monospace', letterSpacing: '0.4px',
            borderRadius: 5, cursor: 'pointer', border: '1px solid', transition: 'all 0.15s',
            background: value === val
              ? (val === 'TARGET_HIT' ? 'rgba(0,229,160,0.15)' : val === 'SL_HIT' ? 'rgba(255,77,109,0.15)' : 'rgba(255,255,255,0.06)')
              : 'transparent',
            color: value === val
              ? (val === 'TARGET_HIT' ? '#00e5a0' : val === 'SL_HIT' ? '#ff4d6d' : '#e8edf5')
              : 'var(--muted2)',
            borderColor: value === val
              ? (val === 'TARGET_HIT' ? 'rgba(0,229,160,0.4)' : val === 'SL_HIT' ? 'rgba(255,77,109,0.4)' : 'rgba(255,255,255,0.15)')
              : 'var(--border)',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN ORDERS PAGE
══════════════════════════════════════════════ */
export default function Orders() {
  const {
    orders, loading, error,
    filters, pagination,
    applyFilter, resetFilters,
    goToPage,
  } = useOrders();

  const { user } = useAuth();
  const meData  = user?.data ?? user ?? {};
  const brokers = meData.brokers ?? [];

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sortCol,       setSortCol]       = useState('created_at');
  const [sortDir,       setSortDir]       = useState('desc');

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortIcon = (col) => {
    if (sortCol !== col) return <span className={styles.sortInactive}>⇅</span>;
    return <span className={styles.sortActive}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const sorted = [...orders].sort((a, b) => {
    let av = a[sortCol];
    let bv = b[sortCol];
    if (sortCol === 'total_pnl' || sortCol === 'net_credit') {
      av = (av !== null && av !== undefined) ? av : -Infinity;
      bv = (bv !== null && bv !== undefined) ? bv : -Infinity;
    }
    if (sortCol === 'created_at') { av = new Date(av); bv = new Date(bv); }
    if (av < bv) return sortDir === 'asc' ? -1 :  1;
    if (av > bv) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const displayed = (filters.squareoff_trigger ?? '')
    ? sorted.filter(o => (o.squareoff_trigger ?? o.addon_exit?.trigger) === filters.squareoff_trigger)
    : sorted;

  /* ── FIX: parens around ?? expression ── */
  const totalPages = (pagination.total_pages) ?? (Math.ceil(pagination.total / pagination.limit) || 1);

  /* derive unique strategies and instruments from loaded orders */
  const strategies = React.useMemo(() => {
    const seen = new Map();
    for (const o of orders) {
      const val = o.strategy_id ?? o.strategy_type;
      if (val && !seen.has(val)) {
        seen.set(val, { value: val, label: o.strategy_name ?? o.strategy_type ?? val });
      }
    }
    return [...seen.values()];
  }, [orders]);

  const instrumentNames = React.useMemo(() =>
    [...new Set(orders.map(o => o.instrument).filter(Boolean))].sort()
  , [orders]);

  const COLS = [
    { key: 'created_at',  label: 'Date',      sortable: true  },
    { key: 'instrument',  label: 'Instrument', sortable: true  },
    { key: 'expiry',      label: 'Expiry',     sortable: false },
    { key: 'signal',      label: 'Signal',     sortable: false },
    { key: 'strategy',    label: 'Strategy',   sortable: false },
    { key: 'broker_name', label: 'Broker',     sortable: false },
    { key: 'strikes',     label: 'Strikes',    sortable: false },
    { key: 'quantity',    label: 'Qty',        sortable: false },
    { key: 'total_pnl',   label: 'PnL',        sortable: true  },
    { key: 'status',      label: 'Status',     sortable: false },
  ];

  return (
    <div className={styles.page}>

      {/* Header — no refresh button */}
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.breadcrumb}>
            <Link to="/dashboard" className={styles.breadcrumbLink}>Dashboard</Link>
            <span className={styles.breadcrumbSep}>›</span>
            <span>Orders</span>
          </div>
          <h1 className={styles.pageTitle}>Orders</h1>
          <p className={styles.pageSubtitle}>
            {loading ? 'Loading…' : `${pagination.total ?? orders.length} total records`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        applyFilter={applyFilter}
        resetFilters={resetFilters}
        brokers={brokers}
        strategies={strategies}
        instrumentNames={instrumentNames}
      />
      <TriggerFilterBar value={filters.squareoff_trigger ?? ''} onSelect={v => applyFilter('squareoff_trigger', v)} />

      {/* Error */}
      {error && <div className={styles.errorBanner}>⚠ {error}</div>}

      {/* Count bar */}
      {!loading && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 4px', marginBottom: 6,
          fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--muted2)',
        }}>
          <span>
            {(() => {
              const total  = pagination.total ?? orders.length;
              const limit  = pagination.limit ?? 20;
              const page   = pagination.page  ?? 1;
              const start  = (page - 1) * limit + 1;
              const end    = (page - 1) * limit + displayed.length;
              if (total === 0) return 'No orders found';
              if (displayed.length === 0) return 'No orders match current filters';
              return `Showing ${start}–${end} of ${total} orders`;
            })()}
          </span>
          {totalPages > 1 && (
            <span>Page {pagination.page} of {totalPages}</span>
          )}
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              {COLS.map(col => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  style={{ cursor: col.sortable ? 'pointer' : 'default' }}
                >
                  {col.label}{col.sortable && <> {sortIcon(col.key)}</>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className={styles.skeletonRow}>
                  {COLS.map((_, j) => (
                    <td key={j}>
                      <span className={styles.skeleton} style={{ width: `${50 + (j * 9) % 35}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : displayed.length === 0 ? (
              <tr>
                <td colSpan={COLS.length} className={styles.empty}>
                  No orders found
                </td>
              </tr>
            ) : (
              displayed.map(order => {
                const id         = order._id;
                const sellStrike = order.sell_leg?.strike;
                const buyStrike  = order.buy_leg?.strike;
                const strikes    = (sellStrike && buyStrike)
                  ? `${sellStrike} / ${buyStrike}`
                  : (sellStrike ?? buyStrike ?? '—');

                return (
                  <tr
                    key={id}
                    className={styles.row}
                    onClick={() => setSelectedOrder(order)}
                  >
                    <td className={styles.tdMuted}>
                      {order.created_at?.slice(0, 10) ?? '—'}
                    </td>
                    <td>
                      <div className={styles.instrCell}>
                        <span className={styles.instrName}>{order.instrument}</span>
                        <span className={styles.instrType}>{order.option_type}</span>
                      </div>
                    </td>
                    <td className={styles.tdMuted}>{order.expiry}</td>
                    <td><SignalPill signal={order.signal} /></td>
                    <td>
                      <Tooltip content={order.strategy_name ?? order.strategy_type ?? '—'}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                          background: 'rgba(245,166,35,0.1)', color: '#f5a623',
                          border: '1px solid rgba(245,166,35,0.25)',
                          fontFamily: 'DM Mono, monospace', cursor: 'default', letterSpacing: '0.5px',
                        }}>
                          {order.strategy_type ?? '—'}
                        </span>
                      </Tooltip>
                    </td>
                    <td><BrokerPill name={order.broker_name} /></td>
                    <td className={styles.tdMono}>{strikes}</td>
                    <td className={styles.tdMono}>{order.sell_leg?.quantity ?? order.buy_leg?.quantity ?? order.lot_size ?? '—'}</td>
                    <td className={styles.tdMono}>
                      {(() => {
                        const hasExit  = !!order.addon_exit;
                        const exitQty  = order.addon_exit?.exit_qty;
                        const totalQty = order.lot_size;
                        const stored   = order.partial_profit != null;

                        let tip = '';
                        if (hasExit) {
                          const trig = (order.squareoff_trigger ?? order.addon_exit.trigger ?? '').replace('_', ' ');
                          const when = fmtDate(order.addon_exit.exited_at);
                          const lines = [];
                          if (stored) {
                            const remainPnl = (order.total_pnl ?? 0) - order.partial_profit;
                            lines.push(`Partial  ${exitQty ?? '?'}/${totalQty ?? '?'} qty   ${formatPnL(order.partial_profit)}`);
                            lines.push(`Remain   ${totalQty && exitQty ? totalQty - exitQty : '?'}/${totalQty ?? '?'} qty   ${formatPnL(remainPnl)}`);
                            lines.push('─────────────────────');
                            lines.push(`Total          ${formatPnL(order.total_pnl)}`);
                          } else {
                            if (exitQty && totalQty) lines.push(`Partial qty    ${exitQty} of ${totalQty}`);
                            if (trig)  lines.push(`Trigger        ${trig}`);
                            if (when)  lines.push(`Partial at     ${when}`);
                            lines.push('─────────────────────');
                            lines.push(`Total P&L      ${formatPnL(order.total_pnl)}`);
                          }
                          tip = lines.join('\n');
                        }

                        return (
                          <Tooltip content={tip}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, cursor: 'default' }}>
                              <span style={{ color: getPnLColor(order.total_pnl), fontWeight: 600 }}>
                                {formatPnL(order.total_pnl)}
                              </span>
                              {hasExit && (
                                <span style={{ fontSize: 10, color: stored ? getPnLColor(order.partial_profit) : '#8892a4', fontFamily: 'DM Mono, monospace', opacity: 0.85 }}>
                                  {stored
                                    ? `↳ ${formatPnL(order.partial_profit)} partial`
                                    : exitQty && totalQty
                                      ? `↳ ${exitQty}/${totalQty} qty partial`
                                      : '↳ partial exit'}
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        );
                      })()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        <StatusPill status={order.status} />
                        {order.addon_exit && (() => {
                          const trig     = order.squareoff_trigger ?? order.addon_exit.trigger;
                          const isTarget = trig === 'TARGET_HIT';
                          const isSL     = trig === 'SL_HIT';
                          const color    = isTarget ? '#00e5a0' : isSL ? '#ff4d6d' : '#f5a623';
                          const bg       = isTarget ? 'rgba(0,229,160,0.08)' : isSL ? 'rgba(255,77,109,0.08)' : 'rgba(245,166,35,0.08)';
                          const border   = isTarget ? 'rgba(0,229,160,0.25)' : isSL ? 'rgba(255,77,109,0.25)' : 'rgba(245,166,35,0.25)';
                          const icon     = isTarget ? '⊕' : isSL ? '⊗' : '⊟';
                          const label    = isTarget ? 'TARGET HIT' : isSL ? 'SL HIT' : (trig ?? 'PARTIAL EXIT');
                          return (
                            <Tooltip content={`Exit qty: ${order.addon_exit.exit_qty ?? '—'}\nExited: ${fmtDate(order.addon_exit.exited_at)}\nBroker: ${order.addon_exit.broker_order_status ?? '—'}`}>
                              <span style={{
                                fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                                background: bg, color, border: `1px solid ${border}`,
                                fontFamily: 'DM Mono, monospace', letterSpacing: '0.5px', cursor: 'default',
                                whiteSpace: 'nowrap',
                              }}>
                                {icon} {label}
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)}>
            ‹ Prev
          </button>
          <span>{pagination.page} / {totalPages}</span>
          <button disabled={pagination.page >= totalPages} onClick={() => goToPage(pagination.page + 1)}>
            Next ›
          </button>
        </div>
      )}

      {/* Drawer */}
      <OrderDrawer
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />

    </div>
  );
}